import React, { useEffect, useRef } from "react";
import { toast } from "top-brains-ui";
import { useNetworkMonitor } from "@src/hooks/useNetworkMonitor";
import { shouldTriggerNotification } from "@src/utils/networkMonitorUtils";
import { NETWORK_STATUS_MESSAGES } from "./networkStatusConstants";

interface NetworkStatusManagerProps {
  currentPathname?: string;
}

/**
 * Checks if the current URL path is an active quiz, test, exam, tournament, or assessment screen.
 */
export const isQuizOrTestRoute = (pathname?: string): boolean => {
  const currentPath =
    pathname ||
    (typeof window !== "undefined"
      ? window.location.hash
        ? window.location.hash.replace(/^#/, "")
        : window.location.pathname
      : "");

  const lower = currentPath.toLowerCase();

  return (
    lower.includes("quiz-screen") ||
    lower.includes("tournament-screen") ||
    lower.includes("exam") ||
    lower.includes("assignment-screen") ||
    lower.includes("student-code-screen") ||
    lower.includes("mock-test") ||
    lower.includes("mockquiz")
  );
};

/**
 * Global Network Status Manager
 * Listens to network quality state transitions and triggers debounced toast notifications
 * ONLY when a student is taking a quiz, exam, or test.
 * Renders null (invisible background manager mounted in App.tsx).
 */
const NetworkStatusManager: React.FC<NetworkStatusManagerProps> = ({
  currentPathname,
}) => {
  const { status, isOnline, downlinkSpeedMbps, speedReductionPercent, config } = useNetworkMonitor();

  const prevStatusRef = useRef<string>(status);
  const lastSlowToastTimeRef = useRef<number>(0);
  const lastPoorToastTimeRef = useRef<number>(0);
  const wasOfflineRef = useRef<boolean>(!isOnline);

  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = status;

    const inQuizOrTest = isQuizOrTestRoute(currentPathname);

    // Transition to OFFLINE
    if (status === "OFFLINE") {
      if (prevStatus !== "OFFLINE") {
        wasOfflineRef.current = true;
        if (inQuizOrTest) {
          toast.error("Network", NETWORK_STATUS_MESSAGES.OFFLINE);
        }
      }
      return;
    }

    // Transition from OFFLINE to ONLINE (GOOD / SLOW / POOR)
    if (wasOfflineRef.current && isOnline) {
      wasOfflineRef.current = false;
      if (inQuizOrTest) {
        toast.success("Network", NETWORK_STATUS_MESSAGES.RESTORED);
      }
    }

    const formatSpeedDetail = () => {
      const parts: string[] = [];
      if (downlinkSpeedMbps !== undefined) {
        parts.push(`~${downlinkSpeedMbps.toFixed(1)} Mbps`);
      }
      if (speedReductionPercent !== undefined && speedReductionPercent > 0) {
        parts.push(`${speedReductionPercent}% reduced`);
      }
      return parts.length > 0 ? ` (${parts.join(", ")})` : "";
    };

    // Handle POOR state notifications
    if (status === "POOR") {
      const now = Date.now();
      const shouldToast =
        prevStatus !== "POOR" ||
        shouldTriggerNotification(
          lastPoorToastTimeRef.current,
          config.notificationCooldownMs,
        );

      if (shouldToast) {
        lastPoorToastTimeRef.current = now;
        if (inQuizOrTest) {
          toast.warning(
            "Network",
            `${NETWORK_STATUS_MESSAGES.POOR}${formatSpeedDetail()}`,
          );
        }
      }
    }
    // Handle SLOW state notifications
    else if (status === "SLOW") {
      const now = Date.now();
      const shouldToast =
        prevStatus !== "SLOW" ||
        shouldTriggerNotification(
          lastSlowToastTimeRef.current,
          config.notificationCooldownMs,
        );

      if (shouldToast) {
        lastSlowToastTimeRef.current = now;
        if (inQuizOrTest) {
          toast.warning(
            "Network",
            `${NETWORK_STATUS_MESSAGES.SLOW}${formatSpeedDetail()}`,
          );
        }
      }
    }
    // Speed restored from SLOW or POOR to GOOD
    else if (status === "GOOD" && (prevStatus === "SLOW" || prevStatus === "POOR")) {
      if (inQuizOrTest) {
        toast.success(
          "Network",
          "Internet speed restored. Connection is stable and good.",
        );
      }
    }
  }, [
    status,
    isOnline,
    downlinkSpeedMbps,
    speedReductionPercent,
    config.notificationCooldownMs,
    currentPathname,
  ]);

  return null;
};

export default NetworkStatusManager;
